import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { PageHeader } from '@/components/shared/PageHeader';
import { CommonNamesInput } from '@/components/species/CommonNamesInput';
import { ArrowLeft, Save, Loader2, Fish, Upload, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/Tooltip';
import {
    useSpeciesById,
    useCreateSpecies,
    useUpdateSpecies,
    useUploadSpeciesImage,
    useSpeciesDetails,
    useUpsertSpeciesDetails,
    normalizeSpeciesImageFilename,
} from './hooks/useSpeciesAdmin';
import { SpeciesExtendedInfoForm } from './components/SpeciesExtendedInfoForm';
import {
    SpeciesSeoFields,
    readSpeciesSeoFaq,
    speciesSeoFaqForSave,
    type SpeciesSeoFaqDraft,
} from './components/SpeciesSeoFields';
import { SpeciesDetailTabBar } from './components/SpeciesNav';
import { SpeciesAppDataPanel } from './components/SpeciesAppDataPanel';
import { SpeciesVideosPanel } from './components/SpeciesVideosPanel';
import {
    emptyExtendedInfoFormState,
    extendedInfoToFormState,
    formStateToExtendedInfo,
    hasExtendedInfoContent,
} from './speciesExtendedInfo';
import type { ExtendedInfoFormState } from './speciesExtendedInfo';
import type { WaterType, Rarity } from './types';
import { WATER_TYPE_LABELS, RARITY_LABELS } from './types';

const STORAGE_BASE = 'https://aipecesprost.blob.core.windows.net/species-images/';
const WATER_OPTIONS: WaterType[] = ['salada', 'dulce', 'both'];
const RARITY_OPTIONS: Rarity[] = ['legendario', 'épico', 'raro', 'común', 'unknown'];

function resolveSpeciesRoute(id: string | undefined, pathname: string) {
    const isNew = id === 'new' || pathname.endsWith('/species/new');
    if (isNew) {
        return { isNew: true as const, speciesId: null };
    }
    const parsed = Number(id);
    if (!Number.isInteger(parsed) || parsed <= 0) {
        return { isNew: false as const, speciesId: null, invalidRoute: true as const };
    }
    return { isNew: false as const, speciesId: parsed };
}

export function SpeciesDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const route = resolveSpeciesRoute(id, pathname);
    const { isNew, speciesId } = route;
    const invalidRoute = 'invalidRoute' in route && route.invalidRoute;
    const { data: species, isLoading } = useSpeciesById(speciesId);
    const { data: details, isLoading: isDetailsLoading } = useSpeciesDetails(speciesId);
    const createMutation = useCreateSpecies();
    const updateMutation = useUpdateSpecies();
    const uploadMutation = useUploadSpeciesImage();
    const upsertDetailsMutation = useUpsertSpeciesDetails();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [scientificName, setScientificName] = useState('');
    const [commonNamesEs, setCommonNamesEs] = useState('');
    const [commonNamesEn, setCommonNamesEn] = useState('');
    const [waterType, setWaterType] = useState<WaterType>('salada');
    const [rarity, setRarity] = useState<Rarity>('común');
    const [isActive, setIsActive] = useState(true);
    const [needsReview, setNeedsReview] = useState(false);
    const [isInAlbum, setIsInAlbum] = useState(false);
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [imageFilename, setImageFilename] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [descriptionEs, setDescriptionEs] = useState('');
    const [descriptionEn, setDescriptionEn] = useState('');
    const [seoTitle, setSeoTitle] = useState('');
    const [seoDescription, setSeoDescription] = useState('');
    const [seoFaqs, setSeoFaqs] = useState<SpeciesSeoFaqDraft[]>([]);
    const [mainGuideSlug, setMainGuideSlug] = useState('');
    const [extendedInfoForm, setExtendedInfoForm] = useState<ExtendedInfoFormState>(emptyExtendedInfoFormState);
    const [activeTab, setActiveTab] = useState<'catalog' | 'details' | 'app' | 'video'>('catalog');
    const [imageLoadFailed, setImageLoadFailed] = useState(false);

    useEffect(() => {
        if (!isNew && species) {
            setScientificName(species.scientific_name || '');
            setCommonNamesEs(species.common_names_es || '');
            setCommonNamesEn(species.common_names_en || '');
            setWaterType(species.water_type);
            setRarity(species.rarity);
            setIsActive(species.is_active);
            setNeedsReview(species.needs_review);
            setIsInAlbum(species.is_in_album ?? false);
            setImageUrl(species.image_url || null);
            setImageFilename(species.image_filename || null);
        }
    }, [isNew, species]);

    useEffect(() => {
        if (details) {
            setDescriptionEs(details.description_es || '');
            setDescriptionEn(details.description_en || '');
            setSeoTitle(details.seo_title || '');
            setSeoDescription(details.seo_description || '');
            setSeoFaqs(readSpeciesSeoFaq(details.seo_faq));
            setMainGuideSlug(typeof details.extended_info?.mainGuideSlug === 'string' ? details.extended_info.mainGuideSlug : '');
            setExtendedInfoForm(extendedInfoToFormState(details.extended_info));
        } else if (!isNew && !isDetailsLoading) {
            setDescriptionEs('');
            setDescriptionEn('');
            setSeoTitle('');
            setSeoDescription('');
            setSeoFaqs([]);
            setMainGuideSlug('');
            setExtendedInfoForm(emptyExtendedInfoFormState());
        }
    }, [details, isNew, isDetailsLoading]);

    const catalogFallbackImageUrl = !isNew && scientificName
        ? `${STORAGE_BASE}${normalizeSpeciesImageFilename(scientificName.trim(), 'png')}`
        : null;
    const effectiveImageUrl = imageUrl
        || (imageFilename ? `${STORAGE_BASE}${imageFilename}` : null)
        || catalogFallbackImageUrl;
    const showImagePreview = Boolean(effectiveImageUrl) && !imageLoadFailed;

    const handleImageUpload = async (files: FileList | null) => {
        if (!files?.[0] || !scientificName.trim()) return;
        const file = files[0];
        if (!file.type.startsWith('image/')) return;
        const ext = file.name.split('.').pop() || 'png';
        const path = normalizeSpeciesImageFilename(scientificName.trim(), ext);
        try {
            const url = await uploadMutation.mutateAsync({ path, file });
            setImageUrl(url);
            setImageFilename(path);
            setImageLoadFailed(false);
        } catch (e: unknown) {
            setSaveError(e instanceof Error ? e.message : 'Error al subir imagen');
        }
    };

    const buildCatalogPayload = () => ({
        p_scientific_name: scientificName.trim(),
        p_common_names_es: commonNamesEs.trim() || null,
        p_common_names_en: commonNamesEn.trim() || null,
        p_water_type: waterType,
        p_rarity: rarity,
        p_image_filename: imageFilename,
        p_image_url: imageUrl,
        p_is_active: isActive,
        p_needs_review: needsReview,
        p_is_in_album: isInAlbum,
    });

    const hasDetailsPayload = () => {
        const extended = formStateToExtendedInfo(extendedInfoForm);
        return Boolean(
            descriptionEs.trim() ||
            descriptionEn.trim() ||
            seoTitle.trim() ||
            seoDescription.trim() ||
            mainGuideSlug.trim() ||
            speciesSeoFaqForSave(seoFaqs).length > 0 ||
            hasExtendedInfoContent(extended)
        );
    };

    const saveDetails = async (catalogId: number) => {
        const extended_info = formStateToExtendedInfo(extendedInfoForm);
        const guideSlug = mainGuideSlug.trim().toLowerCase();
        if (guideSlug) extended_info.mainGuideSlug = guideSlug;
        await upsertDetailsMutation.mutateAsync({
            p_species_catalog_id: catalogId,
            p_description_es: descriptionEs.trim() || null,
            p_description_en: descriptionEn.trim() || null,
            p_extended_info: extended_info,
            p_seo_title: seoTitle.trim(),
            p_seo_description: seoDescription.trim(),
            p_seo_faq: speciesSeoFaqForSave(seoFaqs),
        });
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaveError(null);
        if (!scientificName.trim()) {
            setSaveError('Nombre científico requerido');
            return;
        }
        if (!isNew && isDetailsLoading) {
            setSaveError('Cargando ficha detallada, espera un momento');
            return;
        }
        try {
            if (isNew) {
                const result = await createMutation.mutateAsync(buildCatalogPayload());
                if (result?.id) {
                    if (hasDetailsPayload()) {
                        await saveDetails(result.id);
                    }
                    navigate(`/species/${result.id}`, { replace: true });
                }
            } else if (speciesId != null) {
                await updateMutation.mutateAsync({
                    p_id: speciesId,
                    ...buildCatalogPayload(),
                });
                await saveDetails(speciesId);
            }
        } catch (err: unknown) {
            setSaveError(err instanceof Error ? err.message : 'Error al guardar');
        }
    };

    const isSaving = createMutation.isPending || updateMutation.isPending || upsertDetailsMutation.isPending;
    const isUploading = uploadMutation.isPending;
    const saveDisabled = isSaving || (!isNew && isDetailsLoading);

    useEffect(() => {
        setImageLoadFailed(false);
    }, [effectiveImageUrl]);

    if (invalidRoute) {
        return (
            <div className="space-y-4 max-w-3xl">
                <PageHeader title="Especie no encontrada" description="El identificador de la URL no es válido." />
                <button
                    onClick={() => navigate('/species')}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground bg-muted/50"
                >
                    <ArrowLeft size={14} />
                    Volver al catálogo
                </button>
            </div>
        );
    }

    if (!isNew && isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[200px]">
                <Loader2 size={24} className="animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!isNew && !isLoading && !species) {
        return (
            <div className="space-y-4 max-w-3xl">
                <PageHeader title="Especie no encontrada" description="No existe ninguna especie con ese identificador en el catálogo." />
                <button
                    onClick={() => navigate('/species')}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground bg-muted/50"
                >
                    <ArrowLeft size={14} />
                    Volver al catálogo
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title={isNew ? 'Nueva especie' : (species?.common_names_es || species?.scientific_name || 'Editar especie')}
                description={isNew ? 'Añadir especie al catálogo y ficha detallada' : 'Editar catálogo, descripciones y bloques de la ficha'}
            >
                <button
                    onClick={() => navigate('/species')}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground bg-muted/50"
                >
                    <ArrowLeft size={14} />
                    Volver
                </button>
            </PageHeader>

            <TooltipProvider delayDuration={200}>
            <form onSubmit={handleSave} className="space-y-8 max-w-7xl">
                {saveError && (
                    <div className="p-4 rounded-xl bg-destructive/10 text-destructive text-sm font-medium">{saveError}</div>
                )}

                {!isNew && (
                    <SpeciesDetailTabBar
                        activeTab={activeTab}
                        onChange={setActiveTab}
                        showAppTab
                    />
                )}

                {(isNew || activeTab === 'catalog') && (
                <>
                <div className="rounded-2xl overflow-hidden border border-border bg-gradient-to-b from-muted/30 to-muted/10 shadow-sm">
                    <div
                        className="aspect-[2/1] sm:aspect-[3/1] min-h-[200px] flex flex-col items-center justify-center overflow-hidden cursor-pointer group"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={e => handleImageUpload(e.target.files)}
                        />
                        {showImagePreview ? (
                            <img
                                src={effectiveImageUrl!}
                                alt=""
                                className="w-full h-full object-contain p-6 group-hover:opacity-90 transition-opacity"
                                onError={() => setImageLoadFailed(true)}
                            />
                        ) : (
                            <>
                                <Fish size={48} className="text-muted-foreground/50 mb-2" />
                                <span className="text-sm text-muted-foreground text-center px-4">
                                    {isUploading
                                        ? 'Subiendo...'
                                        : scientificName.trim()
                                            ? 'Clic para subir imagen (PNG/JPG recomendado)'
                                            : 'Introduce el nombre científico arriba para poder subir imagen'}
                                </span>
                                <Upload size={16} className="mt-1 text-muted-foreground/40" />
                                {scientificName.trim() && (
                                    <span className="text-xs text-muted-foreground/70 mt-2 px-4 text-center">
                                        Se guardará como {normalizeSpeciesImageFilename(scientificName.trim())}
                                    </span>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className="rounded-xl border border-border bg-card/50 p-6 shadow-sm space-y-4 max-w-3xl">
                    <h3 className="text-base font-semibold text-foreground border-b border-border pb-2">Catálogo (species_catalog)</h3>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Nombre científico *</label>
                        <input
                            type="text"
                            value={scientificName}
                            onChange={e => setScientificName(e.target.value)}
                            placeholder="Thunnus thynnus"
                            className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                            required
                        />
                    </div>
                    <CommonNamesInput
                        value={commonNamesEs}
                        onChange={setCommonNamesEs}
                        label="Nombres comunes (ES)"
                        placeholder="Escribe un nombre y pulsa Enter o +"
                        id="common-names-es"
                    />
                    <CommonNamesInput
                        value={commonNamesEn}
                        onChange={setCommonNamesEn}
                        label="Nombres comunes (EN)"
                        placeholder="Type a name and press Enter or +"
                        id="common-names-en"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1">Tipo de agua</label>
                            <select
                                value={waterType}
                                onChange={e => setWaterType(e.target.value as WaterType)}
                                className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                            >
                                {WATER_OPTIONS.map(w => (
                                    <option key={w} value={w}>{WATER_TYPE_LABELS[w]}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1">Rareza (valor en BD)</label>
                            <select
                                value={rarity}
                                onChange={e => setRarity(e.target.value as Rarity)}
                                className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                            >
                                {RARITY_OPTIONS.map(r => (
                                    <option key={r} value={r}>{RARITY_LABELS[r]} ({r})</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="rounded border-border" />
                            <span className="text-sm">Activa</span>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="inline-flex text-muted-foreground hover:text-foreground cursor-help" tabIndex={0}><Info size={14} /></span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[220px]">Visible en la app. Si está desactivada, no aparece en listados.</TooltipContent>
                            </Tooltip>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={needsReview} onChange={e => setNeedsReview(e.target.checked)} className="rounded border-border" />
                            <span className="text-sm">Necesita revisión</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={isInAlbum} onChange={e => setIsInAlbum(e.target.checked)} className="rounded border-border" />
                            <span className="text-sm">Flag catálogo: en álbum</span>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="inline-flex text-muted-foreground hover:text-foreground cursor-help" tabIndex={0}><Info size={14} /></span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[240px]">Marcador en species_catalog. La lista Cromos real se gestiona en la pestaña App o en Álbum Cromos.</TooltipContent>
                            </Tooltip>
                        </label>
                    </div>
                </div>
                </>
                )}

                {(isNew || activeTab === 'details') && (
                <>
                <div className="rounded-xl border border-border bg-card/50 p-6 shadow-sm space-y-4">
                    <h3 className="text-base font-semibold text-foreground border-b border-border pb-2">Ficha detallada (species_details)</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1">Descripción (ES)</label>
                            <textarea
                                value={descriptionEs}
                                onChange={e => setDescriptionEs(e.target.value)}
                                placeholder="Descripción en español..."
                                rows={6}
                                className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm resize-y"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1">Descripción (EN)</label>
                            <textarea
                                value={descriptionEn}
                                onChange={e => setDescriptionEn(e.target.value)}
                                placeholder="Description in English..."
                                rows={6}
                                className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm resize-y"
                            />
                        </div>
                    </div>
                </div>

                <div className="rounded-xl border border-border bg-card/50 p-6 shadow-sm space-y-4">
                    <h3 className="text-base font-semibold text-foreground border-b border-border pb-2">Bloques extendidos (extended_info)</h3>
                    <SpeciesExtendedInfoForm value={extendedInfoForm} onChange={setExtendedInfoForm} />
                </div>

                <div className="rounded-xl border border-border bg-card/50 p-6 shadow-sm space-y-2">
                    <label className="block text-sm font-medium text-foreground" htmlFor="species-main-guide-slug">
                        Guía principal (slug del blog)
                    </label>
                    <input
                        id="species-main-guide-slug"
                        type="text"
                        value={mainGuideSlug}
                        onChange={(e) => setMainGuideSlug(e.target.value)}
                        placeholder="pesca-de-la-lubina-desde-costa-cuando-donde-y-con-que"
                        className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                        Si se rellena, esa lectura sale la primera en la ficha. Si se deja vacío, se usa el post enabled cuyo slug empieza por pesca-de-la-, pesca-del- o pesca-de- más el nombre común.
                    </p>
                </div>

                <SpeciesSeoFields
                    seoTitle={seoTitle}
                    seoDescription={seoDescription}
                    faqs={seoFaqs}
                    onSeoTitleChange={setSeoTitle}
                    onSeoDescriptionChange={setSeoDescription}
                    onFaqsChange={setSeoFaqs}
                />
                </>
                )}

                {!isNew && activeTab === 'video' && speciesId != null && (
                    <SpeciesVideosPanel
                        speciesId={speciesId}
                        scientificName={scientificName}
                        commonNameHint={commonNamesEs}
                    />
                )}

                {!isNew && activeTab === 'app' && speciesId != null && (
                    <SpeciesAppDataPanel
                        speciesCatalogId={speciesId}
                        scientificName={scientificName}
                        waterType={waterType}
                    />
                )}

                {(isNew || (activeTab !== 'app' && activeTab !== 'video')) && (
                <div className="flex gap-2">
                    <button
                        type="submit"
                        disabled={saveDisabled}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    >
                        {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        {isSaving ? 'Guardando...' : 'Guardar especie'}
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/species')}
                        className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground bg-muted/50"
                    >
                        Cancelar
                    </button>
                </div>
                )}
            </form>
            </TooltipProvider>
        </div>
    );
}
